/**
 * The Virtual Microphone: How the Other Side Hears You (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "The Virtual Microphone: How the Other Side Hears You",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "In Me and Both modes, the other side hears your translation because Sokuji sends its translated voice to a virtual microphone, and you choose that virtual microphone as the microphone in your meeting app. Your real microphone stays selected in Sokuji. How the virtual microphone is set up depends on your system. Face-to-face (Both, beside me) uses no virtual microphone: the translation plays in the other person’s earbud."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji uses VB-CABLE, a free virtual audio driver. While it is missing, Sokuji asks to install it when it starts (<strong>Install Now</strong> or <strong>Download Manually</strong>); allow the installation and restart Windows if asked. Sokuji then sends its voice to <strong>CABLE Input</strong> by itself. In the meeting app, choose <strong>CABLE Output (VB-Audio Virtual Cable)</strong> as the microphone.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "The Sokuji installer adds a virtual audio driver. In the meeting app, choose <strong>SokujiVirtualAudio</strong> as the microphone. If the driver is installed but macOS has not loaded it, Sokuji says so and offers <strong>Repair</strong>: enter your administrator password and Sokuji restarts Core Audio to load it. If Repair does not help, restart the Mac or run the installer again."
    },
    {
      "title": "Linux",
      "content": "Sokuji creates <strong>Sokuji_Virtual_Mic</strong> through PulseAudio or PipeWire. Choose it as the microphone in the meeting app. If Sokuji reports that <code>pactl</code> is missing, install it (for example <code>sudo apt install pulseaudio-utils</code>) and click Retry."
    },
    {
      "title": "Browser extension",
      "content": "On supported meeting sites, the extension adds <strong>Sokuji Virtual Microphone</strong> to the site’s microphone list. Choose it there. See the <a href=\"/docs/supported-sites\">supported sites</a>."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Select the virtual microphone in the meeting app, never as Sokuji’s own microphone. Sokuji warns you if you try.",
      "In the desktop app, the Translation the other side hears row of the Speech block names the virtual microphone to pick in your meeting app.",
      "With Translation the other side hears off, nothing is spoken, so nothing reaches the virtual microphone.",
      "Make a short test call and check that the other side hears the translated voice."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "The other side hears nothing. What should I check?",
        "answer": "That the meeting app’s microphone is the virtual microphone for your system, that Translation the other side hears is on, that Sokuji is in Me or Both mode, and that a session is running."
      },
      {
        "question": "Does the other side hear my original voice?",
        "answer": "Not by default: only the translation goes to the virtual microphone. Passthrough can mix your own voice in quietly."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "虚拟麦克风：对方如何听到你",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "在我和两者模式下，对方之所以能听到你的翻译，是因为 Sokuji 把翻译语音发送到一个虚拟麦克风，而你在会议应用中把这个虚拟麦克风选作麦克风。Sokuji 中仍然选择你的真实麦克风。虚拟麦克风的设置方式取决于你的系统。面对面（两者，就在身边）不使用虚拟麦克风：翻译在对方的耳机里播放。"
  },
  "steps": [
    {
      "title": "Windows：VB-CABLE",
      "content": "Sokuji 使用免费的虚拟音频驱动 VB-CABLE。只要尚未安装，Sokuji 启动时都会询问是否安装（<strong>Install Now</strong> 或 <strong>Download Manually</strong>）；请允许安装，如果系统提示则重启 Windows。之后 Sokuji 会自动把语音发送到 <strong>CABLE Input</strong>。在会议应用中，将麦克风设为 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>。",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Sokuji 安装程序会添加一个虚拟音频驱动。在会议应用中，将麦克风设为 <strong>SokujiVirtualAudio</strong>。如果驱动已安装但 macOS 没有加载它，Sokuji 会提示并提供<strong>修复</strong>：输入管理员密码后，Sokuji 重启 Core Audio 加载驱动。修复无效时，重启 Mac 或重新运行安装程序。"
    },
    {
      "title": "Linux",
      "content": "Sokuji 通过 PulseAudio 或 PipeWire 创建 <strong>Sokuji_Virtual_Mic</strong>。在会议应用中将它选作麦克风。如果 Sokuji 提示缺少 <code>pactl</code>，请安装它（例如 <code>sudo apt install pulseaudio-utils</code>），然后点击“重试”。"
    },
    {
      "title": "浏览器扩展",
      "content": "在支持的会议网站上，扩展会把 <strong>Sokuji Virtual Microphone</strong> 添加到网站的麦克风列表中。在那里选择它即可。请参阅<a href=\"/docs/supported-sites\">支持的网站</a>。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "请在会议应用中选择虚拟麦克风，绝不要把它设为 Sokuji 自己的麦克风。如果你这样做，Sokuji 会发出警告。",
      "在桌面应用中，语音区块的“对方听到的翻译”这一行会写出要在会议应用中选择的虚拟麦克风。",
      "关闭“对方听到的翻译”后不会朗读任何内容，因此虚拟麦克风不会收到任何声音。",
      "进行一次简短的测试通话，确认对方能听到翻译语音。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "对方什么也听不到，应该检查什么？",
        "answer": "检查会议应用的麦克风是否为你系统对应的虚拟麦克风，“对方听到的翻译”是否已开启，Sokuji 是否处于我或两者模式，以及会话是否正在运行。"
      },
      {
        "question": "对方会听到我的原声吗？",
        "answer": "默认不会：只有翻译会发送到虚拟麦克风。原声直通可以把你自己的声音以较低音量混入。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "仮想マイク：相手にあなたの声が届くしくみ",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "自分モードと両方モードで相手に翻訳が聞こえるのは、Sokuji が翻訳音声を仮想マイクに送り、あなたが会議アプリのマイクにその仮想マイクを選んでいるからです。Sokuji では実際のマイクを選んだままにします。仮想マイクの設定方法はシステムによって異なります。対面（両方、隣にいる）では仮想マイクを使いません。翻訳は相手のイヤホンで再生されます。"
  },
  "steps": [
    {
      "title": "Windows：VB-CABLE",
      "content": "Sokuji は無料の仮想オーディオドライバー VB-CABLE を使います。未インストールの間は、Sokuji が起動時にインストールを勧めます（<strong>Install Now</strong> または <strong>Download Manually</strong>）。インストールを許可し、求められたら Windows を再起動してください。その後は Sokuji が自動で <strong>CABLE Input</strong> に音声を送ります。会議アプリでは、マイクに <strong>CABLE Output (VB-Audio Virtual Cable)</strong> を選びます。",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Sokuji のインストーラーが仮想オーディオドライバーを追加します。会議アプリでは、マイクに <strong>SokujiVirtualAudio</strong> を選びます。ドライバーはインストールされているのに macOS に読み込まれていない場合、Sokuji はその旨を知らせて<strong>修復</strong>を提案します。管理者パスワードを入力すると、Sokuji が Core Audio を再起動してドライバーを読み込みます。修復でも直らない場合は、Mac を再起動するか、インストーラーをもう一度実行してください。"
    },
    {
      "title": "Linux",
      "content": "Sokuji は PulseAudio または PipeWire を通じて <strong>Sokuji_Virtual_Mic</strong> を作成します。会議アプリでこれをマイクに選んでください。<code>pactl</code> が見つからないと Sokuji に表示された場合は、インストールして（例：<code>sudo apt install pulseaudio-utils</code>）「再試行」をクリックします。"
    },
    {
      "title": "ブラウザー拡張機能",
      "content": "対応している会議サイトでは、拡張機能がサイトのマイク一覧に <strong>Sokuji Virtual Microphone</strong> を追加します。そこで選んでください。<a href=\"/docs/supported-sites\">対応サイト</a>をご覧ください。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "仮想マイクは会議アプリで選び、Sokuji 自身のマイクには決して選ばないでください。選ぼうとすると Sokuji が警告します。",
      "デスクトップアプリでは、「音声」ブロックの「相手に聞こえる翻訳」の行に、会議アプリで選ぶ仮想マイクの名前が表示されます。",
      "「相手に聞こえる翻訳」がオフの場合は何も読み上げられないため、仮想マイクには何も届きません。",
      "短いテスト通話をして、翻訳音声が相手に聞こえるか確認してください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "相手に何も聞こえません。何を確認すればよいですか？",
        "answer": "会議アプリのマイクがお使いのシステムの仮想マイクになっているか、「相手に聞こえる翻訳」がオンになっているか、Sokuji が自分モードか両方モードになっているか、セッションが実行中かを確認してください。"
      },
      {
        "question": "相手に自分の元の声は聞こえますか？",
        "answer": "既定では聞こえません。仮想マイクに送られるのは翻訳だけです。原音パススルーを使うと、自分の声を小さな音量で混ぜることができます。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "가상 마이크: 상대방이 내 목소리를 듣는 방법",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "나 모드와 모두 모드에서 상대방이 번역을 들을 수 있는 것은 Sokuji가 번역된 음성을 가상 마이크로 보내고, 내가 회의 앱의 마이크로 그 가상 마이크를 선택하기 때문입니다. Sokuji에서는 실제 마이크를 그대로 선택해 둡니다. 가상 마이크를 설정하는 방법은 시스템에 따라 다릅니다. 대면 대화(모두, 내 옆)에서는 가상 마이크를 사용하지 않으며, 번역은 상대방의 이어버드에서 재생됩니다."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji는 무료 가상 오디오 드라이버인 VB-CABLE을 사용합니다. 설치되지 않은 동안에는 Sokuji가 시작할 때 설치를 권합니다(<strong>Install Now</strong> 또는 <strong>Download Manually</strong>). 설치를 허용하고, 요청하면 Windows를 다시 시작하세요. 그러면 Sokuji가 알아서 <strong>CABLE Input</strong>으로 음성을 보냅니다. 회의 앱에서는 마이크로 <strong>CABLE Output (VB-Audio Virtual Cable)</strong>을 선택하세요.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Sokuji 설치 프로그램이 가상 오디오 드라이버를 추가합니다. 회의 앱에서 마이크로 <strong>SokujiVirtualAudio</strong>를 선택하세요. 드라이버는 설치되어 있지만 macOS가 불러오지 않았다면 Sokuji가 이를 알리고 <strong>복구</strong>를 제안합니다. 관리자 비밀번호를 입력하면 Sokuji가 Core Audio를 재시작하여 드라이버를 불러옵니다. 복구로도 해결되지 않으면 Mac을 다시 시작하거나 설치 프로그램을 다시 실행하세요."
    },
    {
      "title": "Linux",
      "content": "Sokuji는 PulseAudio 또는 PipeWire를 통해 <strong>Sokuji_Virtual_Mic</strong>을 만듭니다. 회의 앱에서 이것을 마이크로 선택하세요. Sokuji가 <code>pactl</code>이 없다고 알리면 설치한 뒤(예: <code>sudo apt install pulseaudio-utils</code>) “다시 시도”를 클릭하세요."
    },
    {
      "title": "브라우저 확장 프로그램",
      "content": "지원되는 회의 사이트에서는 확장 프로그램이 사이트의 마이크 목록에 <strong>Sokuji Virtual Microphone</strong>을 추가합니다. 거기서 선택하세요. <a href=\"/docs/supported-sites\">지원 사이트</a>를 참고하세요."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "가상 마이크는 회의 앱에서 선택하고, 절대 Sokuji 자체의 마이크로 선택하지 마세요. 그렇게 하려고 하면 Sokuji가 경고합니다.",
      "데스크톱 앱에서는 “음성” 블록의 “상대방에게 들리는 번역” 행에 회의 앱에서 선택할 가상 마이크의 이름이 표시됩니다.",
      "“상대방에게 들리는 번역”이 꺼져 있으면 아무것도 음성으로 나오지 않으므로 가상 마이크에도 아무것도 전달되지 않습니다.",
      "짧은 테스트 통화를 해서 상대방에게 번역된 음성이 들리는지 확인하세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "상대방에게 아무것도 들리지 않습니다. 무엇을 확인해야 하나요?",
        "answer": "회의 앱의 마이크가 내 시스템의 가상 마이크로 되어 있는지, “상대방에게 들리는 번역”이 켜져 있는지, Sokuji가 나 모드나 모두 모드인지, 세션이 실행 중인지 확인하세요."
      },
      {
        "question": "상대방에게 내 원래 목소리가 들리나요?",
        "answer": "기본적으로는 들리지 않습니다. 가상 마이크로는 번역만 전달됩니다. 원음 패스스루를 사용하면 내 목소리를 작게 섞을 수 있습니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "El micrófono virtual: cómo te oye la otra parte",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "En los modos Yo y Ambos, la otra parte oye tu traducción porque Sokuji envía su voz traducida a un micrófono virtual, y tú eliges ese micrófono virtual como micrófono en tu aplicación de reuniones. Tu micrófono real sigue seleccionado en Sokuji. Cómo se configura el micrófono virtual depende de tu sistema. En una conversación cara a cara (Ambos, a mi lado) no se usa ningún micrófono virtual: la traducción suena en el auricular de la otra persona."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji usa VB-CABLE, un controlador de audio virtual gratuito. Mientras no esté instalado, Sokuji te pide instalarlo al arrancar (<strong>Install Now</strong> o <strong>Download Manually</strong>); permite la instalación y reinicia Windows si te lo pide. Después, Sokuji envía su voz a <strong>CABLE Input</strong> por sí solo. En la aplicación de reuniones, elige <strong>CABLE Output (VB-Audio Virtual Cable)</strong> como micrófono.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "El instalador de Sokuji añade un controlador de audio virtual. En la aplicación de reuniones, elige <strong>SokujiVirtualAudio</strong> como micrófono. Si el controlador está instalado pero macOS no lo ha cargado, Sokuji lo indica y ofrece <strong>Reparar</strong>: introduce tu contraseña de administrador y Sokuji reinicia Core Audio para cargarlo. Si Reparar no ayuda, reinicia el Mac o vuelve a ejecutar el instalador."
    },
    {
      "title": "Linux",
      "content": "Sokuji crea <strong>Sokuji_Virtual_Mic</strong> mediante PulseAudio o PipeWire. Elígelo como micrófono en la aplicación de reuniones. Si Sokuji indica que falta <code>pactl</code>, instálalo (por ejemplo <code>sudo apt install pulseaudio-utils</code>) y haz clic en Reintentar."
    },
    {
      "title": "Extensión de navegador",
      "content": "En los sitios de reuniones compatibles, la extensión añade <strong>Sokuji Virtual Microphone</strong> a la lista de micrófonos del sitio. Elígelo allí. Consulta los <a href=\"/docs/supported-sites\">sitios compatibles</a>."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Selecciona el micrófono virtual en la aplicación de reuniones, nunca como micrófono del propio Sokuji. Sokuji te avisa si lo intentas.",
      "En la aplicación de escritorio, la fila Traducción que oye la otra parte del bloque Voz indica el micrófono virtual que debes elegir en tu aplicación de reuniones.",
      "Con Traducción que oye la otra parte desactivada no se pronuncia nada, así que nada llega al micrófono virtual.",
      "Haz una llamada de prueba corta y comprueba que la otra parte oye la voz traducida."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "La otra parte no oye nada. ¿Qué debo comprobar?",
        "answer": "Que el micrófono de la aplicación de reuniones sea el micrófono virtual de tu sistema, que Traducción que oye la otra parte esté activada, que Sokuji esté en el modo Yo o Ambos y que haya una sesión en marcha."
      },
      {
        "question": "¿La otra parte oye mi voz original?",
        "answer": "No de forma predeterminada: solo la traducción va al micrófono virtual. Paso directo puede mezclar tu propia voz a bajo volumen."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Le microphone virtuel : comment votre interlocuteur vous entend",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "En modes Moi et Les deux, votre interlocuteur entend votre traduction parce que Sokuji envoie sa voix traduite vers un microphone virtuel, que vous choisissez comme microphone dans votre application de réunion. Votre vrai microphone reste sélectionné dans Sokuji. La configuration du microphone virtuel dépend de votre système. Le face à face (Les deux, à côté de moi) n’utilise pas de microphone virtuel : la traduction est lue dans l’écouteur de l’autre personne."
  },
  "steps": [
    {
      "title": "Windows : VB-CABLE",
      "content": "Sokuji utilise VB-CABLE, un pilote audio virtuel gratuit. Tant qu’il est absent, Sokuji propose de l’installer au démarrage (<strong>Install Now</strong> ou <strong>Download Manually</strong>) ; autorisez l’installation et redémarrez Windows si on vous le demande. Sokuji envoie ensuite de lui-même sa voix vers <strong>CABLE Input</strong>. Dans l’application de réunion, choisissez <strong>CABLE Output (VB-Audio Virtual Cable)</strong> comme microphone.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Le programme d’installation de Sokuji ajoute un pilote audio virtuel. Dans l’application de réunion, choisissez <strong>SokujiVirtualAudio</strong> comme microphone. Si le pilote est installé mais que macOS ne l’a pas chargé, Sokuji le signale et propose <strong>Réparer</strong> : entrez votre mot de passe administrateur et Sokuji redémarre Core Audio pour le charger. Si Réparer n’aide pas, redémarrez le Mac ou relancez le programme d’installation."
    },
    {
      "title": "Linux",
      "content": "Sokuji crée <strong>Sokuji_Virtual_Mic</strong> via PulseAudio ou PipeWire. Choisissez-le comme microphone dans l’application de réunion. Si Sokuji signale que <code>pactl</code> est absent, installez-le (par exemple <code>sudo apt install pulseaudio-utils</code>) et cliquez sur Réessayer."
    },
    {
      "title": "Extension de navigateur",
      "content": "Sur les sites de réunion pris en charge, l’extension ajoute <strong>Sokuji Virtual Microphone</strong> à la liste des microphones du site. Choisissez-le là. Voir les <a href=\"/docs/supported-sites\">sites pris en charge</a>."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Sélectionnez le microphone virtuel dans l’application de réunion, jamais comme microphone de Sokuji lui-même. Sokuji vous avertit si vous essayez.",
      "Dans l’application de bureau, la ligne Traduction entendue par l'autre du bloc Voix indique le microphone virtuel à choisir dans votre application de réunion.",
      "Avec Traduction entendue par l'autre désactivée, rien n’est prononcé, donc rien n’arrive au microphone virtuel.",
      "Faites un court appel de test et vérifiez que votre interlocuteur entend la voix traduite."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Mon interlocuteur n’entend rien. Que dois-je vérifier ?",
        "answer": "Que le microphone de l’application de réunion est le microphone virtuel de votre système, que Traduction entendue par l'autre est activée, que Sokuji est en mode Moi ou Les deux, et qu’une session est en cours."
      },
      {
        "question": "Mon interlocuteur entend-il ma voix originale ?",
        "answer": "Pas par défaut : seule la traduction va vers le microphone virtuel. Le Passage direct peut y mêler discrètement votre propre voix."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "الميكروفون الافتراضي: كيف يسمعك الطرف الآخر",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "في وضعي أنا والكل، يسمع الطرف الآخر ترجمتك لأن Sokuji يرسل صوته المترجَم إلى ميكروفون افتراضي، وأنت تختار ذلك الميكروفون الافتراضي ميكروفونًا في تطبيق الاجتماع. أما في Sokuji فيبقى ميكروفونك الحقيقي هو المحدد. تعتمد طريقة إعداد الميكروفون الافتراضي على نظامك. أما المحادثة وجهًا لوجه (الكل، بجانبي) فلا تستخدم ميكروفونًا افتراضيًا: تُشغَّل الترجمة في سماعة أذن الشخص الآخر."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "يستخدم Sokuji برنامج VB-CABLE، وهو برنامج تشغيل صوت افتراضي مجاني. ما دام غير مثبّت، يطلب Sokuji تثبيته عند بدء تشغيله (<strong>Install Now</strong> أو <strong>Download Manually</strong>)؛ اسمح بالتثبيت وأعد تشغيل Windows إذا طُلب منك ذلك. بعد ذلك يرسل Sokuji صوته إلى <strong>CABLE Input</strong> تلقائيًا. في تطبيق الاجتماع، اختر <strong>CABLE Output (VB-Audio Virtual Cable)</strong> ميكروفونًا.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "يضيف مثبّت Sokuji برنامج تشغيل صوت افتراضيًا. في تطبيق الاجتماع، اختر <strong>SokujiVirtualAudio</strong> ميكروفونًا. إذا كان برنامج التشغيل مثبتًا لكن macOS لم يحمّله، فيخبرك Sokuji بذلك ويعرض عليك <strong>إصلاح</strong>: أدخل كلمة مرور المسؤول، فيعيد Sokuji تشغيل Core Audio لتحميله. إذا لم يُجدِ الإصلاح، فأعد تشغيل جهاز Mac أو شغّل المثبّت مرة أخرى."
    },
    {
      "title": "Linux",
      "content": "ينشئ Sokuji الميكروفون <strong>Sokuji_Virtual_Mic</strong> عبر PulseAudio أو PipeWire. اختره ميكروفونًا في تطبيق الاجتماع. إذا أبلغ Sokuji بأن <code dir=\"ltr\">pactl</code> غير موجود، فثبّته (مثلًا <code dir=\"ltr\">sudo apt install pulseaudio-utils</code>) وانقر على «إعادة المحاولة»."
    },
    {
      "title": "امتداد المتصفح",
      "content": "في مواقع الاجتماعات المدعومة، يضيف الامتداد <strong>Sokuji Virtual Microphone</strong> إلى قائمة الميكروفونات في الموقع. اختره هناك. راجع <a href=\"/docs/supported-sites\">المواقع المدعومة</a>."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "اختر الميكروفون الافتراضي في تطبيق الاجتماع، ولا تختره أبدًا ميكروفونًا لـ Sokuji نفسه. يحذّرك Sokuji إذا حاولت ذلك.",
      "في تطبيق سطح المكتب، يذكر صف «الترجمة التي يسمعها الطرف الآخر» في قسم الكلام اسم الميكروفون الافتراضي الذي تختاره في تطبيق الاجتماع.",
      "عندما تكون «الترجمة التي يسمعها الطرف الآخر» متوقفة لا يُنطق شيء، فلا يصل شيء إلى الميكروفون الافتراضي.",
      "أجرِ مكالمة اختبار قصيرة وتأكد من أن الطرف الآخر يسمع الصوت المترجَم."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "الطرف الآخر لا يسمع شيئًا. ماذا أتحقق منه؟",
        "answer": "تحقّق من أن ميكروفون تطبيق الاجتماع هو الميكروفون الافتراضي الخاص بنظامك، وأن «الترجمة التي يسمعها الطرف الآخر» مفعّلة، وأن Sokuji في وضع أنا أو الكل، وأن هناك جلسة جارية."
      },
      {
        "question": "هل يسمع الطرف الآخر صوتي الأصلي؟",
        "answer": "ليس افتراضيًا: الترجمة وحدها تذهب إلى الميكروفون الافتراضي. يمكن لـ«التمرير المباشر» أن يمزج صوتك بهدوء."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "O microfone virtual: como a outra pessoa ouve você",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Nos modos Eu e Ambos, a outra pessoa ouve a sua tradução porque o Sokuji envia a voz traduzida para um microfone virtual, e você escolhe esse microfone virtual como microfone no seu aplicativo de reunião. Seu microfone real continua selecionado no Sokuji. A configuração do microfone virtual depende do seu sistema. Em uma conversa cara a cara (Ambos, ao meu lado), nenhum microfone virtual é usado: a tradução toca no fone da outra pessoa."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "O Sokuji usa o VB-CABLE, um driver de áudio virtual gratuito. Enquanto ele não estiver instalado, o Sokuji pede para instalá-lo ao iniciar (<strong>Install Now</strong> ou <strong>Download Manually</strong>); permita a instalação e reinicie o Windows se for solicitado. Depois, o Sokuji envia sozinho a voz para o <strong>CABLE Input</strong>. No aplicativo de reunião, escolha <strong>CABLE Output (VB-Audio Virtual Cable)</strong> como microfone.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "O instalador do Sokuji adiciona um driver de áudio virtual. No aplicativo de reunião, escolha <strong>SokujiVirtualAudio</strong> como microfone. Se o driver está instalado mas o macOS não o carregou, o Sokuji avisa e oferece <strong>Reparar</strong>: digite sua senha de administrador e o Sokuji reinicia o Core Audio para carregá-lo. Se Reparar não ajudar, reinicie o Mac ou execute o instalador de novo."
    },
    {
      "title": "Linux",
      "content": "O Sokuji cria o <strong>Sokuji_Virtual_Mic</strong> pelo PulseAudio ou PipeWire. Escolha-o como microfone no aplicativo de reunião. Se o Sokuji informar que o <code>pactl</code> está faltando, instale-o (por exemplo <code>sudo apt install pulseaudio-utils</code>) e clique em Tentar novamente."
    },
    {
      "title": "Extensão de navegador",
      "content": "Nos sites de reunião compatíveis, a extensão adiciona o <strong>Sokuji Virtual Microphone</strong> à lista de microfones do site. Escolha-o ali. Veja os <a href=\"/docs/supported-sites\">sites compatíveis</a>."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Selecione o microfone virtual no aplicativo de reunião, nunca como o microfone do próprio Sokuji. O Sokuji avisa se você tentar.",
      "No aplicativo de desktop, a linha Tradução que o outro lado ouve do bloco Voz mostra o nome do microfone virtual a escolher no seu aplicativo de reunião.",
      "Com Tradução que o outro lado ouve desativada, nada é falado, então nada chega ao microfone virtual.",
      "Faça uma chamada de teste curta e confira se a outra pessoa ouve a voz traduzida."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "A outra pessoa não ouve nada. O que devo verificar?",
        "answer": "Se o microfone do aplicativo de reunião é o microfone virtual do seu sistema, se Tradução que o outro lado ouve está ativada, se o Sokuji está no modo Eu ou Ambos e se há uma sessão em andamento."
      },
      {
        "question": "A outra pessoa ouve a minha voz original?",
        "answer": "Não por padrão: só a tradução vai para o microfone virtual. A Passagem direta pode misturar sua própria voz em volume baixo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Виртуальный микрофон: как собеседник слышит вас",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "В режимах «Я» и «Оба» собеседник слышит ваш перевод, потому что Sokuji отправляет переведённый голос на виртуальный микрофон, а вы выбираете этот виртуальный микрофон в качестве микрофона в приложении для встреч. В самом Sokuji остаётся выбранным ваш настоящий микрофон. Как настраивается виртуальный микрофон, зависит от вашей системы. При разговоре лицом к лицу («Оба», «Рядом со мной») виртуальный микрофон не используется: перевод звучит в наушнике собеседника."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji использует VB-CABLE, бесплатный виртуальный аудиодрайвер. Пока он не установлен, Sokuji при запуске предлагает его установить (<strong>Install Now</strong> или <strong>Download Manually</strong>); разрешите установку и перезапустите Windows, если потребуется. После этого Sokuji сам отправляет свой голос на <strong>CABLE Input</strong>. В приложении для встреч выберите микрофоном <strong>CABLE Output (VB-Audio Virtual Cable)</strong>.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Установщик Sokuji добавляет виртуальный аудиодрайвер. В приложении для встреч выберите микрофоном <strong>SokujiVirtualAudio</strong>. Если драйвер установлен, но macOS его не загрузила, Sokuji сообщает об этом и предлагает <strong>Исправить</strong>: введите пароль администратора, и Sokuji перезапустит Core Audio, чтобы загрузить его. Если «Исправить» не помогло, перезагрузите Mac или запустите установщик ещё раз."
    },
    {
      "title": "Linux",
      "content": "Sokuji создаёт <strong>Sokuji_Virtual_Mic</strong> через PulseAudio или PipeWire. Выберите его микрофоном в приложении для встреч. Если Sokuji сообщает, что <code>pactl</code> отсутствует, установите его (например, <code>sudo apt install pulseaudio-utils</code>) и нажмите «Повторить»."
    },
    {
      "title": "Расширение для браузера",
      "content": "На поддерживаемых сайтах для встреч расширение добавляет <strong>Sokuji Virtual Microphone</strong> в список микрофонов сайта. Выберите его там. См. <a href=\"/docs/supported-sites\">поддерживаемые сайты</a>."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Выбирайте виртуальный микрофон в приложении для встреч, но никогда — как микрофон самого Sokuji. Если попытаться, Sokuji предупредит.",
      "В настольном приложении строка «Перевод, который слышит собеседник» в блоке «Речь» называет виртуальный микрофон, который нужно выбрать в приложении для встреч.",
      "Когда «Перевод, который слышит собеседник» выключен, ничего не озвучивается, поэтому на виртуальный микрофон ничего не поступает.",
      "Сделайте короткий тестовый звонок и проверьте, что собеседник слышит переведённый голос."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Собеседник ничего не слышит. Что проверить?",
        "answer": "Что в приложении для встреч выбран виртуальный микрофон вашей системы, что «Перевод, который слышит собеседник» включён, что Sokuji в режиме «Я» или «Оба» и что сеанс запущен."
      },
      {
        "question": "Слышит ли собеседник мой исходный голос?",
        "answer": "По умолчанию нет: на виртуальный микрофон идёт только перевод. Функция Passthrough может тихо подмешивать ваш собственный голос."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Das virtuelle Mikrofon: wie Ihr Gegenüber Sie hört",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "In den Modi „Ich“ und „Beides“ hört Ihr Gegenüber Ihre Übersetzung, weil Sokuji seine übersetzte Stimme an ein virtuelles Mikrofon sendet und Sie dieses virtuelle Mikrofon in Ihrer Meeting-App als Mikrofon wählen. In Sokuji bleibt Ihr echtes Mikrofon ausgewählt. Wie das virtuelle Mikrofon eingerichtet wird, hängt von Ihrem System ab. Von Angesicht zu Angesicht („Beides“, „Neben mir“) wird kein virtuelles Mikrofon verwendet: Die Übersetzung wird im Ohrhörer der anderen Person abgespielt."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji verwendet VB-CABLE, einen kostenlosen virtuellen Audiotreiber. Solange er fehlt, fragt Sokuji beim Start, ob er installiert werden soll (<strong>Install Now</strong> oder <strong>Download Manually</strong>); lassen Sie die Installation zu und starten Sie Windows neu, falls Sie dazu aufgefordert werden. Danach sendet Sokuji seine Stimme automatisch an <strong>CABLE Input</strong>. Wählen Sie in der Meeting-App <strong>CABLE Output (VB-Audio Virtual Cable)</strong> als Mikrofon.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Das Sokuji-Installationsprogramm fügt einen virtuellen Audiotreiber hinzu. Wählen Sie in der Meeting-App <strong>SokujiVirtualAudio</strong> als Mikrofon. Ist der Treiber installiert, aber von macOS nicht geladen, meldet Sokuji das und bietet <strong>Reparieren</strong> an: Geben Sie Ihr Administratorpasswort ein, und Sokuji startet Core Audio neu, um ihn zu laden. Hilft Reparieren nicht, starten Sie den Mac neu oder führen Sie das Installationsprogramm erneut aus."
    },
    {
      "title": "Linux",
      "content": "Sokuji erstellt <strong>Sokuji_Virtual_Mic</strong> über PulseAudio oder PipeWire. Wählen Sie es in der Meeting-App als Mikrofon. Meldet Sokuji, dass <code>pactl</code> fehlt, installieren Sie es (zum Beispiel <code>sudo apt install pulseaudio-utils</code>) und klicken Sie auf „Wiederholen“."
    },
    {
      "title": "Browsererweiterung",
      "content": "Auf unterstützten Meeting-Seiten fügt die Erweiterung <strong>Sokuji Virtual Microphone</strong> zur Mikrofonliste der Seite hinzu. Wählen Sie es dort. Siehe die <a href=\"/docs/supported-sites\">unterstützten Seiten</a>."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Wählen Sie das virtuelle Mikrofon in der Meeting-App, nie als Mikrofon von Sokuji selbst. Sokuji warnt Sie, wenn Sie es versuchen.",
      "In der Desktop-App nennt die Zeile „Übersetzung, die das Gegenüber hört“ im Bereich „Sprache“ das virtuelle Mikrofon, das Sie in Ihrer Meeting-App wählen.",
      "Ist „Übersetzung, die das Gegenüber hört“ aus, wird nichts gesprochen, also kommt auch nichts am virtuellen Mikrofon an.",
      "Machen Sie einen kurzen Testanruf und prüfen Sie, ob Ihr Gegenüber die übersetzte Stimme hört."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Mein Gegenüber hört nichts. Was sollte ich prüfen?",
        "answer": "Dass das Mikrofon der Meeting-App das virtuelle Mikrofon Ihres Systems ist, dass „Übersetzung, die das Gegenüber hört“ eingeschaltet ist, dass Sokuji im Modus „Ich“ oder „Beides“ ist und dass eine Sitzung läuft."
      },
      {
        "question": "Hört mein Gegenüber meine Originalstimme?",
        "answer": "Standardmäßig nicht: Nur die Übersetzung geht an das virtuelle Mikrofon. Mit der Durchleitung lässt sich Ihre eigene Stimme leise beimischen."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Віртуальний мікрофон: як співрозмовник чує вас",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "У режимах «Я» та «Обидва» співрозмовник чує ваш переклад, тому що Sokuji надсилає перекладений голос на віртуальний мікрофон, а ви вибираєте цей віртуальний мікрофон як мікрофон у застосунку для зустрічей. У самому Sokuji залишається вибраним ваш справжній мікрофон. Як налаштовується віртуальний мікрофон, залежить від вашої системи. Під час розмови віч-на-віч («Обидва», «Поруч зі мною») віртуальний мікрофон не використовується: переклад звучить у навушнику співрозмовника."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji використовує VB-CABLE, безкоштовний віртуальний аудіодрайвер. Поки його не встановлено, Sokuji під час запуску пропонує його встановити (<strong>Install Now</strong> або <strong>Download Manually</strong>); дозвольте встановлення та перезапустіть Windows, якщо буде потрібно. Після цього Sokuji сам надсилає свій голос на <strong>CABLE Input</strong>. У застосунку для зустрічей виберіть мікрофоном <strong>CABLE Output (VB-Audio Virtual Cable)</strong>.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Інсталятор Sokuji додає віртуальний аудіодрайвер. У застосунку для зустрічей виберіть мікрофоном <strong>SokujiVirtualAudio</strong>. Якщо драйвер встановлено, але macOS його не завантажила, Sokuji повідомляє про це й пропонує <strong>Виправити</strong>: введіть пароль адміністратора, і Sokuji перезапустить Core Audio, щоб його завантажити. Якщо виправлення не допомогло, перезавантажте Mac або запустіть інсталятор ще раз."
    },
    {
      "title": "Linux",
      "content": "Sokuji створює <strong>Sokuji_Virtual_Mic</strong> через PulseAudio або PipeWire. Виберіть його мікрофоном у застосунку для зустрічей. Якщо Sokuji повідомляє, що <code>pactl</code> відсутній, установіть його (наприклад, <code>sudo apt install pulseaudio-utils</code>) і натисніть «Повторити»."
    },
    {
      "title": "Розширення для браузера",
      "content": "На підтримуваних сайтах для зустрічей розширення додає <strong>Sokuji Virtual Microphone</strong> до списку мікрофонів сайту. Виберіть його там. Див. <a href=\"/docs/supported-sites\">підтримувані сайти</a>."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Вибирайте віртуальний мікрофон у застосунку для зустрічей, але ніколи — як мікрофон самого Sokuji. Якщо спробувати, Sokuji попередить.",
      "У настільному застосунку рядок «Переклад, який чує співрозмовник» у блоці «Мовлення» називає віртуальний мікрофон, який слід вибрати в застосунку для зустрічей.",
      "Коли «Переклад, який чує співрозмовник» вимкнено, нічого не озвучується, тому на віртуальний мікрофон нічого не надходить.",
      "Зробіть короткий тестовий дзвінок і перевірте, що співрозмовник чує перекладений голос."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Співрозмовник нічого не чує. Що перевірити?",
        "answer": "Що в застосунку для зустрічей вибрано віртуальний мікрофон вашої системи, що «Переклад, який чує співрозмовник» увімкнено, що Sokuji в режимі «Я» або «Обидва» і що сеанс запущено."
      },
      {
        "question": "Чи чує співрозмовник мій оригінальний голос?",
        "answer": "За замовчуванням ні: на віртуальний мікрофон іде лише переклад. Функція Passthrough може тихо підмішувати ваш власний голос."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Il microfono virtuale: come ti sente l’altra parte",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Nelle modalità Io ed Entrambi, l’altra parte sente la tua traduzione perché Sokuji invia la sua voce tradotta a un microfono virtuale, e tu scegli quel microfono virtuale come microfono nella tua app per riunioni. In Sokuji resta selezionato il tuo microfono reale. La configurazione del microfono virtuale dipende dal tuo sistema. Il faccia a faccia (Entrambi, accanto a me) non usa alcun microfono virtuale: la traduzione viene riprodotta nell’auricolare dell’altra persona."
  },
  "steps": [
    {
      "title": "Windows: VB-CABLE",
      "content": "Sokuji usa VB-CABLE, un driver audio virtuale gratuito. Finché manca, Sokuji propone di installarlo all’avvio (<strong>Install Now</strong> o <strong>Download Manually</strong>); consenti l’installazione e riavvia Windows se richiesto. Da quel momento Sokuji invia da solo la sua voce a <strong>CABLE Input</strong>. Nell’app per riunioni, scegli <strong>CABLE Output (VB-Audio Virtual Cable)</strong> come microfono.",
      "screenshot": "01-output-routing.png"
    },
    {
      "title": "macOS",
      "content": "Il programma di installazione di Sokuji aggiunge un driver audio virtuale. Nell’app per riunioni, scegli <strong>SokujiVirtualAudio</strong> come microfono. Se il driver è installato ma macOS non lo ha caricato, Sokuji lo segnala e propone <strong>Ripara</strong>: inserisci la password di amministratore e Sokuji riavvia Core Audio per caricarlo. Se Ripara non aiuta, riavvia il Mac o esegui di nuovo il programma di installazione."
    },
    {
      "title": "Linux",
      "content": "Sokuji crea <strong>Sokuji_Virtual_Mic</strong> tramite PulseAudio o PipeWire. Sceglilo come microfono nell’app per riunioni. Se Sokuji segnala che manca <code>pactl</code>, installalo (per esempio <code>sudo apt install pulseaudio-utils</code>) e fai clic su Riprova."
    },
    {
      "title": "Estensione per browser",
      "content": "Sui siti di riunione supportati, l’estensione aggiunge <strong>Sokuji Virtual Microphone</strong> all’elenco dei microfoni del sito. Sceglilo lì. Vedi i <a href=\"/docs/supported-sites\">siti supportati</a>."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Seleziona il microfono virtuale nell’app per riunioni, mai come microfono di Sokuji stesso. Sokuji ti avvisa se ci provi.",
      "Nell’app desktop, la riga Traduzione che sente l'altra parte del blocco Voce indica il microfono virtuale da scegliere nella tua app per riunioni.",
      "Con Traduzione che sente l'altra parte disattivata non viene pronunciato nulla, quindi nulla arriva al microfono virtuale.",
      "Fai una breve chiamata di prova e verifica che l’altra parte senta la voce tradotta."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "L’altra parte non sente niente. Cosa devo controllare?",
        "answer": "Che il microfono dell’app per riunioni sia il microfono virtuale del tuo sistema, che Traduzione che sente l'altra parte sia attiva, che Sokuji sia in modalità Io o Entrambi e che ci sia una sessione attiva."
      },
      {
        "question": "L’altra parte sente la mia voce originale?",
        "answer": "Non per impostazione predefinita: al microfono virtuale arriva solo la traduzione. Il Passthrough può mescolarvi la tua voce a basso volume."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureVirtualMicrophone() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/virtual-microphone" />;
}
